import React, { useEffect } from 'react'
import { useQuery } from 'react-query'
import { makeCoreGetRequest } from 'utils/api'

type UseSelectSearchProps<DataType> = {
  optionsMap: (item: DataType) => any
  url: string
  initialSearchText?: string
  filterName?: string
  searchMinStringLength?: number
  searchFields?: string[]
}

export const useSelectSearch = <DataType, OptionType>({
  url,
  optionsMap,
  initialSearchText = '',
  filterName = 'filterValue',
  searchMinStringLength = 3,
  searchFields = []
}: UseSelectSearchProps<DataType>) => {
  const [searchText, setSearchText] = React.useState('')
  const [debouncedTerm, setDebouncedTerm] = React.useState('')
  const [timeOutId, setTimeoutId] = React.useState<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setSearchText(initialSearchText)
    setDebouncedTerm(initialSearchText)
  }, [initialSearchText])

  const optionsQuery = useQuery(
    ['url', debouncedTerm],
    async () => {
      const params = searchFields && searchFields.length
        ? { searchBy: searchFields.join('|'), searchValue: debouncedTerm }
        : { [filterName]: debouncedTerm }
      return await makeCoreGetRequest(url, {
        params
      })
    },
    {
      initialData: [],
      enabled: debouncedTerm?.length >= searchMinStringLength,
      keepPreviousData: true
    }
  )

  const options: OptionType[] = Array.isArray(optionsQuery.data)
    ? optionsQuery.data.map(optionsMap)
    : optionsQuery.data?.list?.map(optionsMap) || []

  const onInputChange = (e, value) => {
    setSearchText(value)

    if (e?.type === 'change') {
      if (timeOutId) {
        setTimeoutId(null)
        clearTimeout(timeOutId)
      }

      const id = setTimeout(() => {
        setDebouncedTerm(value)
        setTimeoutId(null)
      }, 300)

      setTimeoutId(id)
    }
  }

  return {
    options,
    onInputChange,
    inputValue: searchText,
    setSearchText
  }
}
